import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { queryOnboardingListingThunk } from '../../ducks/onboarding.duck';

// The dashboard summarises at most this many of the member's most recent
// inquiries. Each one costs a messages query (the Marketplace API cannot
// include messages in transactions/query), so this bounds the page load.
export const MAX_TRANSACTIONS = 50;
// Parallel message queries, kept low to stay clear of API rate limits.
const MESSAGE_QUERY_CONCURRENCY = 5;

// ================ Helper functions ================ //

const relId = (entity, rel) => entity.relationships?.[rel]?.data?.id?.uuid || null;
const toIso = date => (date instanceof Date ? date.toISOString() : date);

/**
 * Reduce a transactions/query response to plain, serialisable summaries —
 * all dashboardMetrics.js needs, and safe to keep in the Redux store.
 */
const summariseTransactions = response =>
  response.data.data.map(tx => ({
    id: tx.id.uuid,
    createdAt: toIso(tx.attributes.createdAt),
    providerId: relId(tx, 'provider'),
    customerId: relId(tx, 'customer'),
  }));

const queryTransactions = (sdk, only) =>
  sdk.transactions
    .query({
      only,
      include: ['provider', 'customer'],
      'fields.transaction': ['createdAt'],
      'fields.user': ['profile.abbreviatedName'],
      perPage: MAX_TRANSACTIONS,
    })
    .then(summariseTransactions);

const queryMessages = (sdk, txId) =>
  sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender'],
      'fields.message': ['createdAt'],
      'fields.user': ['profile.abbreviatedName'],
      perPage: 100,
    })
    .then(response =>
      response.data.data.map(m => ({
        senderId: relId(m, 'sender'),
        createdAt: toIso(m.attributes.createdAt),
      }))
    );

// Run message queries a few at a time rather than all at once.
const queryMessagesForAll = async (sdk, transactions) => {
  const messagesByTxId = {};
  for (let i = 0; i < transactions.length; i += MESSAGE_QUERY_CONCURRENCY) {
    const batch = transactions.slice(i, i + MESSAGE_QUERY_CONCURRENCY);
    const results = await Promise.all(batch.map(tx => queryMessages(sdk, tx.id)));
    batch.forEach((tx, j) => {
      messagesByTxId[tx.id] = results[j];
    });
  }
  return messagesByTxId;
};

// ================ Async Thunks ================ //

// Both sides are queried because loadData runs before currentUser (and so the
// user type) is guaranteed to be in the store. A member only ever has one
// side: providers receive inquiries (sales), food companies send them (orders).
const loadDashboardPayloadCreator = async (_, { rejectWithValue, extra: sdk }) => {
  try {
    const [sales, orders] = await Promise.all([
      queryTransactions(sdk, 'sale'),
      queryTransactions(sdk, 'order'),
    ]);
    const messagesByTxId = await queryMessagesForAll(sdk, [...sales, ...orders]);
    return { sales, orders, messagesByTxId, loadedAt: new Date().toISOString() };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const loadDashboardThunk = createAsyncThunk(
  'DashboardPage/loadDashboard',
  loadDashboardPayloadCreator
);

// ================ Slice ================ //

const dashboardPageSlice = createSlice({
  name: 'DashboardPage',
  initialState: {
    loadInProgress: false,
    loadError: null,
    sales: [],
    orders: [],
    messagesByTxId: {},
    loadedAt: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDashboardThunk.pending, state => {
        state.loadInProgress = true;
        state.loadError = null;
      })
      .addCase(loadDashboardThunk.fulfilled, (state, action) => {
        return { ...state, ...action.payload, loadInProgress: false };
      })
      .addCase(loadDashboardThunk.rejected, (state, action) => {
        state.loadInProgress = false;
        state.loadError = action.payload;
      });
  },
});

export default dashboardPageSlice.reducer;

// ================ Load data ================ //

export const loadData = () => dispatch =>
  Promise.all([
    dispatch(fetchCurrentUser()),
    // Not the .unwrap() wrapper: the thunk's in-flight condition may skip it.
    dispatch(queryOnboardingListingThunk()),
    dispatch(loadDashboardThunk()),
  ]);
